import { API_BASE_URL } from "src/config/api";
import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Grid,
  InputAdornment,
} from '@mui/material';
import PropTypes from 'prop-types';
import DoneIcon from '@mui/icons-material/Done';
import { toast } from 'react-toastify';

EditModal.propTypes = {
  isOpen: PropTypes.bool,
  user: PropTypes.object,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
};

export default function EditModal({ isOpen, user, onClose, onSave }) {
  const initialFormData = {
    fullName: '',
    username: '',
    email: '',
    phoneNumber: '',
  };

  const [formData, setFormData] = useState({ ...initialFormData, ...user });
  const [validationErrors, setValidationErrors] = useState({});
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [isVerifiedLoading, setIsVerifiedLoading] = useState(false);

  const isValidPhoneNumber = (phoneNumber) => {
    const phoneRegex = /^(\+91|0)?[6789]\d{9}$/;
    return phoneRegex.test(phoneNumber);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    return emailRegex.test(email);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    const newValidationErrors = { ...validationErrors };

    if (!value.trim()) {
      newValidationErrors[id] = `${id} is required`;
    } else {
      delete newValidationErrors[id];
    }

    if (id === 'username') {
      if (/\s/.test(value)) {
        newValidationErrors[id] = 'Username should not contain spaces';
      } else if (value !== value.toLowerCase()) {
        newValidationErrors[id] = 'Username should be in all lowercase';
      }
    } else if (id === 'phoneNumber' && !isValidPhoneNumber(value)) {
      newValidationErrors[id] = 'Invalid phone number format';
    }
    if (id === 'fullName') {
      const lettersAndSpaces = value.replace(/[^A-Za-z ]/g, '');
      const letterCount = lettersAndSpaces.replace(/ /g, '').length;
      if (letterCount < 3 || lettersAndSpaces !== value) {
        newValidationErrors[id] = 'Full name should contain at least three letters and no numbers or unknown words';
      }
    }
    if (id === 'email') {
      if (!value.trim()) {
        newValidationErrors[id] = 'Email is required';
      } else if (!isValidEmail(value)) {
        newValidationErrors[id] = 'Invalid email format';
      }
    }
    setValidationErrors(newValidationErrors);

    setFormData({
      ...formData,
      [id]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.email !== user.email) {
      if (!isVerified) {
        toast.error('Email must be verified before submitting the form');
        return;
      }
    }

    if (Object.keys(validationErrors).length === 0) {
      onSave(formData, e);
    }
  };

  const sendOTP = async () => {
    try {
      setIsVerifiedLoading(true);
      // if (isVerified) {
      //   toast.info('Email is already verified');
      //   return;
      // }
      const response = await fetch(`${API_BASE_URL}/sendEmailVerifactionMail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailId: formData.email.toLowerCase() }),
      });
      const data = await response.json();

      if (data.success === true) {
        toast.success(data.message, {
          autoClose: 2000,
        });
        setOtpDialogOpen(true);
      } else {
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Error sending OTP. Please try again later.');
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const verifyOTP = async () => {
    try {
      setIsVerifiedLoading(true);
      const response = await fetch(`${API_BASE_URL}/verifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ otp: verificationCode }),
      });

      if (response.status === 200) {
        toast.success('OTP Verified!', {
          autoClose: 2000,
        });
        setIsVerified(true);
        setOtpDialogOpen(false);
      } else {
        toast.error('Invalid OTP. Please try again.');
      }
    } catch (error) {
      toast.error('An error occurred while verifying OTP. Please try again later.');
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const validateOTP = () => {
    if (!verificationCode) {
      setOtpError('OTP is required');
    } else if (!/^\d{4}$/.test(verificationCode)) {
      setOtpError('OTP must be exactly four digits');
    } else {
      setOtpError('');
      verifyOTP();
    }
  };

  const handleCloseOtpDialog = () => {
    setOtpDialogOpen(false);
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: {
              xs: '90%',
              sm: 400,
              md: 500,
            },
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" align="center" sx={{ mb: 2 }}>Edit Personal Details</Typography>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  id="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  error={!!validationErrors.fullName}
                  helperText={validationErrors.fullName}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email"
                  id="email"
                  value={formData.email}
                  variant="outlined"
                  fullWidth
                  error={!!validationErrors.email}
                  helperText={validationErrors.email}
                  InputProps={{
                    endAdornment: isVerified ? (
                      <InputAdornment position="end">
                        <DoneIcon color="success" />
                      </InputAdornment>
                    ) : (
                      <InputAdornment position="end">
                        {isVerifiedLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Button variant="outlined" onClick={sendOTP} size="small">
                            Verify
                          </Button>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Phone Number"
                  id="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  error={!!validationErrors.phoneNumber}
                  helperText={validationErrors.phoneNumber}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button type="submit" variant="contained" color="primary">
                    Update
                  </Button>
                  <Button onClick={onClose} variant="outlined" color="error" >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Box>
      </Modal>

      <Dialog open={otpDialogOpen} onClose={handleCloseOtpDialog}>
        <DialogTitle>OTP Verification</DialogTitle>
        <DialogContent>
          <TextField
            label="Enter OTP"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            error={!!otpError}
            helperText={otpError}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOtpDialog}>Cancel</Button>
          {isVerifiedLoading ? (
            <CircularProgress />
          ) : (
            <Button onClick={validateOTP} color="primary">
              Verify
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
